import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/i18n/SiteContext";
import { api, errMsg, waLink } from "@/lib/api";
import { Label, inputCls } from "@/components/site/QuickRequestForm";
import { PrivacyConsent } from "@/components/site/PrivacyConsent";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/images";

export default function ContactPage() {
  const { t, lang, settings } = useSite();
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) return toast.error(t("err.required"));
    if (!privacy) return toast.error(t("consent.required"));
    setLoading(true);
    try {
      await api.post("/inquiries", { kind: "contact", ...f, lang, privacy_accepted: true });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err, t("err.generic")));
    } finally {
      setLoading(false);
    }
  };

  const items = [
    settings.phone && { icon: Phone, l: t("contact.phoneL"), v: settings.phone, href: `tel:${settings.phone.replace(/\s/g, "")}`, id: "phone" },
    settings.whatsapp && { icon: MessageCircle, l: t("contact.waL"), v: settings.whatsapp, href: waLink(settings.whatsapp, t("wa.default")), id: "whatsapp", ext: true },
    settings.email && { icon: Mail, l: t("contact.emailL"), v: settings.email, href: `mailto:${settings.email}`, id: "email" },
    settings.address && { icon: MapPin, l: t("contact.addressL"), v: settings.address, id: "address" },
    settings.hours && { icon: Clock, l: t("contact.hoursL"), v: settings.hours, id: "hours" },
  ].filter(Boolean);

  return (
    <div data-testid="contact-page">
      <section className="relative h-[52vh] min-h-[400px] flex items-end overflow-hidden" data-testid="contact-hero">
        <img src={IMAGES.contact} alt="Sorrento, Marina Grande" className="absolute inset-0 w-full h-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <Reveal className="relative max-w-6xl mx-auto w-full px-6 pb-14">
          <p className="text-xs font-medium tracking-super-wide text-sand-brand uppercase mb-4">{t("contact.eyebrow")}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-ivory font-normal leading-tight">{t("contact.title")}</h1>
          <p className="mt-4 text-base md:text-lg text-ivory/85 max-w-xl leading-relaxed">{t("contact.sub")}</p>
        </Reveal>
      </section>

      <section className="px-6 py-20 md:py-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7 bg-white rounded-3xl border border-sand/70 shadow-soft-xl p-8 md:p-12">
            {done ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle2 size={48} className="mx-auto text-sage-dark" />
                <h3 className="font-serif text-3xl text-ocean mt-5">{t("contact.sentTitle")}</h3>
                <p className="mt-3 text-ocean/60">{t("contact.sentText")}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><Label>{t("contact.name")} *</Label><input required className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name-input" /></div>
                  <div><Label>{t("contact.email")} *</Label><input required type="email" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email-input" /></div>
                </div>
                <div><Label>{t("contact.phone")}</Label><input className={inputCls} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="contact-phone-input" /></div>
                <div><Label>{t("contact.message")} *</Label><textarea required rows={5} className={`${inputCls} h-auto py-3 resize-none`} placeholder={t("contact.messagePh")} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message-input" /></div>
                <PrivacyConsent checked={privacy} onChange={setPrivacy} testId="contact-privacy" />
                <button type="submit" disabled={loading} data-testid="contact-submit-button" className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-ocean text-ivory text-sm font-medium tracking-wider uppercase rounded-xl hover:bg-ocean-700 disabled:opacity-60 shadow-soft btn-lift">
                  <Send size={17} /> {loading ? t("contact.sending") : t("contact.send")}
                </button>
              </form>
            )}
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 space-y-4">
            {items.map(({ icon: Icon, l, v, href, id, ext }) => {
              const inner = (
                <div className="flex gap-4 items-start">
                  <div className="w-11 h-11 rounded-full bg-sage-mist flex items-center justify-center shrink-0"><Icon size={18} className="text-sage-dark" /></div>
                  <div><p className="text-[11px] tracking-[0.2em] uppercase text-ocean/50">{l}</p><p className="text-ocean mt-1">{v}</p></div>
                </div>
              );
              return href ? (
                <a key={id} href={href} target={ext ? "_blank" : undefined} rel={ext ? "noopener noreferrer" : undefined} data-testid={`contact-info-${id}`} className="block bg-white rounded-2xl p-5 border border-sand/70 shadow-soft hover:shadow-soft-lg" style={{ transition: "box-shadow .25s ease" }}>{inner}</a>
              ) : (
                <div key={id} className="bg-white rounded-2xl p-5 border border-sand/70 shadow-soft" data-testid={`contact-info-${id}`}>{inner}</div>
              );
            })}
            {settings.whatsapp && (
              <a href={waLink(settings.whatsapp, t("wa.default"))} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-cta" className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-[#25D366] text-white text-sm font-medium tracking-wider uppercase rounded-xl shadow-soft btn-lift">
                <MessageCircle size={18} /> {t("contact.chat")}
              </a>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
